import React from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { ImageModel, LinkModel } from '../../models/globals';
import FacebookIcon from '../../svgs/facebook.svg';
import YouTubeIcon from '../../svgs/youtube.svg';
import LinkedInIcon from '../../svgs/linkedin.svg';
import DownArrow from '../../svgs/downarrow.svg';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { IconLink } from '../common/IconLink';
import { Responsive } from '../common/Responsive';

interface FooterProps {
    model: FooterModel;
}

interface FooterModel {
    courses: FooterSectionModel;
    ed2GoAssistance: FooterSectionModel;
    ed2GoResources: FooterSectionModel;
    logo: ImageModel;
    cengageLinks: LinkModel[];
    socialMediaLinks: FooterSectionModel;
    copyright: string;
    scopedCssUrl: string;
}

interface FooterSectionModel {
    showSection: boolean;
    heading: string;
    links: LinkModel[];
}

const Footer: React.FC<FooterProps> = ({
    model: { courses, ed2GoAssistance, ed2GoResources, cengageLinks, socialMediaLinks, logo, copyright },
}) => {
    return (
        <footer className="footer text-muted small p-5">
            <Container>
                <Row>
                    <Col lg={8}>
                        <div className="d-flex justify-content-start">
                            <div className="w-100">
                                <FooterSection {...courses} />
                            </div>
                            <div className="w-100">
                                <FooterSection {...ed2GoAssistance} />
                            </div>
                            <div className="w-100">
                                <Responsive viewMode="desktop-and-tablet" testIdSuffix="media-section">
                                    <MediaSection {...socialMediaLinks} />
                                </Responsive>
                                <FooterSection {...ed2GoResources} />
                                <Responsive viewMode="mobile" testIdSuffix="media-section">
                                    <MediaSection {...socialMediaLinks} />
                                </Responsive>
                            </div>
                        </div>
                    </Col>
                    <Col lg={4}>
                        <div className="pl-4 e2g-footer_border border-e2gdark">
                            <p>
                                <img src={logo.url} width="89" height="auto" alt={logo.altText} />
                            </p>
                            <ul className="list-unstyled">
                                {cengageLinks.map((link, index) => (
                                    <li key={index} className="pb-2">
                                        <a className="text-white" href={link.url}>
                                            {link.name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            <div className="e2g-footer_copyright">{copyright}</div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </footer>
    );
};

const FooterSection: React.FC<FooterSectionModel> = ({ showSection, heading, links }) => {
    const navbarId = `basic-navbar-nav-${heading.toLowerCase().split(' ').join('-')}`;

    return (
        <>
            {showSection && (
                <Navbar aria-label={heading} expand="sm" className="pl-1 navbar-expand-sm d-print">
                    <Navbar.Brand className="section-header">{heading}</Navbar.Brand>

                    <Navbar.Toggle
                        className="row justify-content-between pl-3 footer-toggle-section"
                        aria-controls={navbarId}
                    >
                        <Navbar.Brand className="section-header-mobile">{heading}</Navbar.Brand>
                        <DownArrow className="collapse-flip size-1" />
                    </Navbar.Toggle>

                    <Navbar.Collapse id={navbarId}>
                        <Nav className="mr-auto">
                            <ul className="list-unstyled">
                                {links.map((link, index) => (
                                    <li key={index} className="pb-2 nav-item">
                                        <Nav.Link className="text-white d-inline" href={link.url}>
                                            {link.name}
                                        </Nav.Link>
                                    </li>
                                ))}
                            </ul>
                        </Nav>
                    </Navbar.Collapse>
                </Navbar>
            )}
        </>
    );
};

const getSocialMediaIcon = (name: string): React.ElementType => {
    switch (name.toUpperCase()) {
        case 'FACEBOOK':
            return FacebookIcon;
        case 'YOUTUBE':
            return YouTubeIcon;
        case 'LINKEDIN':
            return LinkedInIcon;
    }
    return () => null;
};

const MediaSection: React.FC<FooterSectionModel> = ({ showSection, heading, links }) => {
    return (
        <>
            {showSection && (
                <div className="social-media">
                    <p className="text-uppercase section-header-sm">{heading}</p>
                    <ul className="list-unstyled">
                        {links.map((link) => (
                            <li className="d-inline" key={link.name}>
                                <IconLink Icon={getSocialMediaIcon(link.name)} {...link} />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );
};

export { Footer, FooterProps, FooterModel, FooterSectionModel };
